import { useEffect, useState, useCallback, useContext } from "react";

import { RecaptchaState } from "@/models/continente-credentials/mobile/RecaptchaState";
import { Config } from "../../config/Config";
import { DigitalAssetContext } from "../../contexts/continente-credentials/DigitalAssetContext";
import i18next from "i18next";

export const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Google reCAPTCHA global callbacks
window.googleCallback = () => {
  if (!document.getElementById("g-recaptcha")?.hasChildNodes()) {
    window.grecaptcha.render("g-recaptcha", {
      sitekey: siteKey,
      callback: "googleChallengeSuccess",
    });
  }
};

window.googleChallengeSuccess = () => {
  window.dispatchEvent(
    new CustomEvent("googleChallenge", { detail: "successful_chalenge" })
  );
};

export interface CaptchaHookReturn {
  // Common
  captchaProvider: string;
  isRequired: boolean;
  getCaptchaToken: () => string | undefined;
  resetCaptchaRequirement: () => void;
  requestCaptchaRequirement: (force?: boolean) => void;
  flowRequiresCaptcha: boolean;

  // Google reCAPTCHA specific
  useGoogleRecaptcha: (successCallbackFunction?: EventListener | null) => void;

  // Altcha specific
  altchaCaptchaToken: string;
  handleAltchaCaptchaVerified: (response: string) => void;
  handleAltchaCaptchaError: (error: string) => void;

  // Rendering helpers
  shouldShowRecaptcha: boolean;
  shouldShowAltcha: boolean;
}

export function useCaptcha(locationState: RecaptchaState | null): CaptchaHookReturn {
  const digitalAssetContext = useContext(DigitalAssetContext);
  const [altchaCaptchaToken, setAltchaCaptchaToken] = useState<string>("");
  const locationRequiresCaptcha =
    locationState?.properties?.requiredRecaptcha ?? false;
  const [isCaptchaRequired, setIsCaptchaRequired] = useState<boolean>(
    locationRequiresCaptcha
  );
  const [flowRequiresCaptcha, setFlowRequiresCaptcha] = useState<boolean>(
    locationRequiresCaptcha
  );

  const captchaProvider = digitalAssetContext.digitalAsset.captchaProvider || "";

  useEffect(() => {
    setIsCaptchaRequired(locationRequiresCaptcha);
    setFlowRequiresCaptcha(locationRequiresCaptcha);
    if (!locationRequiresCaptcha) {
      setAltchaCaptchaToken("");
    }
  }, [locationRequiresCaptcha]);

  const shouldShowRecaptcha =
    captchaProvider === "recaptcha" && isCaptchaRequired;
  const shouldShowAltcha =
    captchaProvider === "altcha" && isCaptchaRequired;

  const handleAltchaCaptchaVerified = useCallback((response: string) => {
    setAltchaCaptchaToken(response);
  }, []);

  const handleAltchaCaptchaError = useCallback((error: string) => {
    console.error("Altcha captcha error:", error);
    setAltchaCaptchaToken("");
  }, []);

  const getCaptchaToken = useCallback((): string | undefined => {
    if (captchaProvider === "altcha") {
      return altchaCaptchaToken;
    } else if (captchaProvider === "recaptcha") {
      return getRecaptchaToken(locationState);
    }

    return undefined;
  }, [captchaProvider, altchaCaptchaToken, locationState]);

  const resetCaptchaRequirement = useCallback(() => {
    setIsCaptchaRequired(false);
    setAltchaCaptchaToken("");

    if (captchaProvider === "recaptcha") {
      try {
        window.grecaptcha.reset();
      } catch (err) {
        // no-op if widget not yet rendered
      }
    }
  }, [captchaProvider]);

  const requestCaptchaRequirement = useCallback(
    (force = false) => {
      if (force && !flowRequiresCaptcha) {
        setFlowRequiresCaptcha(true);
      }

      if (force || flowRequiresCaptcha) {
        setIsCaptchaRequired(true);
        setAltchaCaptchaToken("");
      }
    },
    [flowRequiresCaptcha]
  );

  const useGoogleRecaptcha = useCallback((successCallbackFunction: EventListener | null = null) => {
    useEffect(() => {
      if (shouldShowRecaptcha) {
        let language = "";

        switch (i18next.language) {
          case "pt":
            language = "&hl=pt";
            break;
          case "en":
            language = "&hl=en";
            break;
          default:
            language = "&hl=pt";
        }

        const script = document.createElement("script");
        script.src = Config.GoogleRecaptcha.Url + language;
        document.body.appendChild(script);
        var styleElement = document.createElement("style");
        styleElement.id = "recaptcha-style";

        // Define your CSS rules
        var cssRules = `
          #g-recaptcha> :first-child {
            padding: 0;
          }
          
          #rc-imageselect {
            transform: scale(0.77);
            -webkit-transform: scale(0.77);
            transform-origin: 0 0;
            -webkit-transform-origin: 0 0;
          }

          body > :last-child {
            left: 0 !important;
            position: absolute !important;
            left: 0 !important;
            right: 0 !important;
            margin-left: auto !important;
            margin-right: auto !important;
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }

          body > :last-child > :last-child {
            display: flex !important;
            justify-content: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
            background-color: white !important;
            border: 1px solid rgb(204, 204, 204);
            box-shadow: 10px 10px 10px rgb(230 230 230);
          }
        `;

        // Append CSS rules to the <style> element
        styleElement.appendChild(document.createTextNode(cssRules));

        // Append the <style> element to the <head> of the document
        document.head.appendChild(styleElement);
      }

      if (successCallbackFunction != null) {
        window.addEventListener("googleChallenge", successCallbackFunction);
      }

      return () => {
        document.getElementById("recaptcha-style")?.remove();

        if (successCallbackFunction != null) {
          window.removeEventListener("googleChallenge", successCallbackFunction);
        }
      };
    }, [shouldShowRecaptcha, successCallbackFunction]);
  }, [shouldShowRecaptcha]);

  return {
    captchaProvider,
    isRequired: isCaptchaRequired,
    flowRequiresCaptcha,
    getCaptchaToken,
    resetCaptchaRequirement,
    requestCaptchaRequirement,
    useGoogleRecaptcha,
    altchaCaptchaToken,
    handleAltchaCaptchaVerified,
    handleAltchaCaptchaError,
    shouldShowRecaptcha,
    shouldShowAltcha,
  };
}

export function getRecaptchaToken(
  locationState: RecaptchaState | null
): string | undefined {
  if (locationState?.properties?.requiredRecaptcha)
    try {
      return window.grecaptcha.getResponse();
    } catch (e) {
      return undefined;
    }

  return undefined;
}

// Keep original exports for backward compatibility
export function useGoogleRecaptcha(
  locationState: RecaptchaState | null,
  successCallbackFunction: EventListener | null = null
) {
  const captcha = useCaptcha(locationState);
  captcha.useGoogleRecaptcha(successCallbackFunction);
}
